import CoachMark from '@@types/CoachMark';

import styles from '~/components/CoachMark/coachMark.module.scss';
import {
  COACH_MARK_THEME,
  CoachMarkName,
  CoachMarkShape,
  CoachMarkType,
  EXPORT_TASK_WITH_AI_COACH_MARK_KEY,
  Placement,
  PopperType,
} from '~/constants';
import { PreferenceCategory } from '~/constants/member';
import i18n from '~/i18n';
import { coachMarkStore } from '~/stores/CoachMarkStore';
import { globalSettingStore } from '~/stores/GlobalSettingStore';
import { uiState } from '~/stores/UiState';
import { userStore } from '~/stores/UserStore';
import domUtil from '~/utils/domUtil';
import { isNonNil } from '~/utils/fnUtil/fnUtil';
import { assoc, pipe, propEq, reject, when } from 'ramda';

import Actions from '../Actions';
import { stores } from '../Stores';

class CoachMarkActions {
  constructor(private actions: Actions) {}

  private closer: (() => void) | null = null;

  complete = async () => {
    const coachMark = globalSettingStore.userPreferences[PreferenceCategory.COACH_MARK];
    const currentStep = coachMarkStore.currentStep;
    coachMarkStore.setSteps([]);
    coachMarkStore.setCurrentIndex(0);
    if (currentStep) {
      currentStep?.onClick?.();
    }
    try {
      if (coachMarkStore.type === CoachMarkType.Main) {
        const newCoachMark = assoc(CoachMarkType.StatusIcon, true, coachMark); // 최초 설치 사용자는 제외

        await this.actions.settingActions.updateCoachMark(assoc(CoachMarkType.Main, true, newCoachMark));
      } else if (coachMarkStore.type === CoachMarkType.InviteChannel) {
        await this.actions.settingActions.updateCoachMark(assoc(CoachMarkType.InviteChannel, true, coachMark));
      } else if (coachMarkStore.type === CoachMarkType.StatusIcon) {
        await this.actions.settingActions.updateCoachMark(assoc(CoachMarkType.StatusIcon, true, coachMark));
      } else if (coachMarkStore.type === CoachMarkType.CloseThread) {
        await this.actions.settingActions.updateCoachMark(assoc(CoachMarkType.CloseThread, true, coachMark));
      } else if (coachMarkStore.type === CoachMarkType.MakeThread) {
        await this.actions.settingActions.updateCoachMark(assoc(CoachMarkType.MakeThread, true, coachMark));
      } else if (coachMarkStore.type === CoachMarkType.ExportTaskWithAI) {
        window.localStorage.setItem(EXPORT_TASK_WITH_AI_COACH_MARK_KEY, 'true');
      }
    } catch {}
  };

  completeMakeThreadCoachMark = () => {
    if (coachMarkStore.currentStep?.name === CoachMarkType.MakeThread) {
      this.complete();
    }
  };

  startMainCoachMark = () => {
    const { canInviteMembers } = stores;
    const validCoachMarks = reject((coachMark: CoachMark) => {
      return (
        (userStore.isGuest && coachMark.name === CoachMarkName.CreateChannel) ||
        (!canInviteMembers && coachMark.name === CoachMarkName.Invite)
      );
    }, this.mainCoachMarks());
    coachMarkStore.setSteps(validCoachMarks);
    coachMarkStore.setType(CoachMarkType.Main);
  };

  startInviteCoachMark = () => {
    pipe(
      when(() => userStore.isGuest, reject(propEq('name', CoachMarkName.InviteChannel))),
      coachMarkStore.setSteps
    )(this.getInviteChannelCoachMarks());
    coachMarkStore.setType(CoachMarkType.InviteChannel);
  };

  startStatusIconCoachMark = () => {
    pipe(coachMarkStore.setSteps)([
      {
        name: CoachMarkName.UserMenuStatusIcon,
        shape: CoachMarkShape.Circle,
        pulsePlacement: Placement.BottomEnd,
        title: i18n.t('coachMark.statusIconTitle'),
        description: i18n.t('coachMark.statusIconDescription'),
        offset: [0, 35],
      },
    ]);
    coachMarkStore.setType(CoachMarkType.StatusIcon);
  };

  startCloseThreadCoachMark = () => {
    coachMarkStore.setSteps([
      {
        name: CoachMarkName.CloseThread,
        shape: CoachMarkShape.ClickableCircle,
        pulsePlacement: Placement.BottomEnd,
        title: i18n.t('coachMark.closeThreadTitle', '글타래 닫기'),
        description: i18n.t('coachMark.closeThreadDescription', 'ESC로 닫아보세요.'),
        offset: [20, 30],
        backdrop: 'none',
        theme: COACH_MARK_THEME.CLOSE_BTN,
      },
    ]);
    coachMarkStore.setType(CoachMarkType.CloseThread);
  };

  startMakeThreadCoachMark = () => {
    coachMarkStore.setSteps([
      {
        name: CoachMarkName.MakeThread,
        shape: CoachMarkShape.ClickableCircle,
        pulsePlacement: Placement.Top,
        title: i18n.t('coachMark.threadInfoTitle', '글타래'),
        description: i18n.t('coachMark.threadInfoDescription', '글타래를 이용해 답장해 보세요.'),
        offset: [0, 30],
        highlight: 'bg',
        theme: COACH_MARK_THEME.CLOSE_BTN,
        onClick: () => {
          uiState.setHoverTriggerLog(null);
          uiState.closePopper(PopperType.LogMenu);
          if (coachMarkStore.currentStep) {
            this.completeMakeThreadCoachMark();
          }
        },
      },
    ]);
    coachMarkStore.setType(CoachMarkType.MakeThread);
  };

  startExportTaskWithAICoachMark = () => {
    coachMarkStore.setSteps([
      {
        name: CoachMarkName.ExportTaskWithAI,
        shape: CoachMarkShape.ClickableCircle,
        pulsePlacement: Placement.BottomEnd,
        title: i18n.t('coachMark.exportTaskWithAITitle', '업무로 내보내기(with AI)'),
        description: i18n.t(
          'coachMark.exportTaskWithAIDescription',
          '글타래에서 나눈 대화를 AI가 정리하고, 업무로 등록할 수 있어요!'
        ),
        offset: [20, 30],
        backdrop: 'none',
        theme: COACH_MARK_THEME.CLOSE_BTN,
      },
    ]);
    coachMarkStore.setType(CoachMarkType.ExportTaskWithAI);
  };

  openPopper = (options) => {
    const { referenceNode, step } = options;
    const additionalModifiers = isNonNil(step.flip)
      ? [
          {
            name: 'flip',
            options: { fallbackPlacements: step.flip },
          },
        ]
      : [];

    this.addHighlightClass(referenceNode, step);
    uiState.openPopper({
      name: PopperType.CoachMark,
      args: {
        referenceNode,
        options: {
          modifiers: [
            { name: 'offset', options: { offset: step.offset } },
            { name: 'preventOverflow', options: { boundary: document.body } },
            ...additionalModifiers,
          ],
          placement: step.placement ?? step.pulsePlacement,
        },
        title: step.title,
        description: step.description,
        onClickNext: () => {
          this.closer?.();
        },
      },
    });
  };

  private addHighlightClass = (referenceNode: HTMLElement, step: CoachMark) => {
    const highlightNode = coachMarkStore.currentHighlightTarget || referenceNode;
    domUtil.addClass(styles[step.shape], highlightNode);

    this.closer = () => {
      domUtil.removeClass(styles[step.shape], highlightNode);
      this.closer = null;
    };
  };

  private setUserMenuOpen = (value: boolean) => {
    uiState.setUserMenuOpen(value);
  };

  private mainCoachMarks: () => CoachMark[] = () => [
    {
      name: CoachMarkName.CreateChannel,
      shape: CoachMarkShape.Circle,
      pulsePlacement: Placement.Top,
      title: i18n.t('coachMark.createChannelTitle'),
      description: i18n.t('coachMark.createChannelDescription'),
      offset: [0, 35],
    },
    {
      name: CoachMarkName.SearchChannel,
      shape: CoachMarkShape.Rect,
      pulsePlacement: Placement.Right,
      title: i18n.t('coachMark.channelSearchTitle'),
      description: i18n.t('coachMark.channelSearchDescription'),
      offset: [0, 25],
    },
    {
      name: CoachMarkName.Invite,
      shape: CoachMarkShape.Rect,
      pulsePlacement: Placement.Right,
      title: i18n.t('coachMark.inviteTitle'),
      description: i18n.t('coachMark.inviteDescription'),
      offset: [0, 25],
      highlight: 'bg',
      handlers: {
        before: () => this.setUserMenuOpen(true),
        after: () => this.setUserMenuOpen(false),
      },
    },
    {
      name: CoachMarkName.Collection,
      shape: CoachMarkShape.Rect,
      pulsePlacement: Placement.Left,
      placement: Placement.LeftStart,
      offset: [0, 25],
      title: i18n.t('coachMark.collectionTitle'),
      description: i18n.t('coachMark.collectionDescription'),
      highlightNode: `.js-coach-mark-${CoachMarkName.Collection}-highlight`,
    },
  ];

  private getInviteChannelCoachMarks = () => {
    return [
      {
        name: CoachMarkName.InviteChannel,
        shape: CoachMarkShape.Rect,
        pulsePlacement: Placement.Left,
        offset: [0, 25],
        flip: ['bottom'],
        title: i18n.t('coachMark.inviteChannelTitle'),
        description: i18n.t('coachMark.inviteChannelDescription'),
        highlight: 'bg',
      },
    ];
  };
}

export default CoachMarkActions;
